import { ApiError } from "./familyDoctors";
import { ohtScopeHeaders } from "../utils/portalRoleChoice";

export { ApiError };

async function requestJson<T>(
  url: string,
  init?: RequestInit
): Promise<T> {
  const res = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init?.headers || {}),
    },
  });

  const text = await res.text().catch(() => "");
  let data: any = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text || null;
  }

  if (!res.ok) {
    const message =
      (data && typeof data.error === "string" && data.error) ||
      `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  return requestJson<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type CommunityNavigatorAvailability =
  | { available: true; ohtName?: string }
  | { available: false };

export async function checkCommunityNavigatorAvailability(
  postalCode: string
): Promise<CommunityNavigatorAvailability> {
  return postJson<CommunityNavigatorAvailability>(
    "/api/public/community-navigator/availability",
    { postalCode }
  );
}

export type CommunityNavigatorRequestPayload = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  postalCode: string;
  phone: string;
  email?: string;
  consentAccepted: true;
};

export type CommunityNavigatorRequestResult = {
  success?: boolean;
  alreadyActive?: boolean;
  available?: boolean;
  message?: string;
};

export async function submitCommunityNavigatorRequest(
  payload: CommunityNavigatorRequestPayload
): Promise<CommunityNavigatorRequestResult> {
  return postJson<CommunityNavigatorRequestResult>(
    "/api/public/community-navigator/requests",
    payload
  );
}

export type NavigatorProviderOperatingHours = {
  dayOfWeek: number;
  openTime: string | null;
  closeTime: string | null;
  isClosed: boolean;
};

export type NavigatorProviderIntakePreference = {
  criterionKey: string | null;
  criterionValue: string | null;
  matchBehavior: string | null;
  isHardRule: boolean;
};

export type NavigatorProviderProfile = {
  familyDoctorId: number;
  doctor: {
    familyDoctorId: number;
    fullName: string | null;
    cpso: string | null;
    gender: string | null;
    acceptingNewPatients: boolean;
    acceptingStatusUpdatedAt: string | null;
    intakeNotes: string | null;
    firstAppointmentBookingUrl: string | null;
    declaredCapacity: number | null;
    remainingCapacityOverride: number | null;
    remainingCapacityUpdatedAt: string | null;
    remainingCapacity: number | null;
    activePipelineCount: number;
  };
  services: {
    languages: string[];
    visitTypes: string[];
    coverages: string[];
  };
  intakePreferences: NavigatorProviderIntakePreference[];
  clinic: {
    clinicId: number;
    name: string | null;
    clinicSetting: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    province: string | null;
    postalCode: string | null;
    phone: string | null;
    fax: string | null;
    website: string | null;
    administrationEmail: string | null;
    operatingHours: NavigatorProviderOperatingHours[];
    accessibilityFeatures: string[];
    onSiteServices: string[];
  };
};

export async function getCommunityNavigatorProviderProfile(
  familyDoctorId: number
): Promise<NavigatorProviderProfile> {
  const id = Number(familyDoctorId);
  if (!Number.isFinite(id) || id < 1) {
    throw new ApiError(400, "A valid provider id is required.");
  }

  return requestJson<NavigatorProviderProfile>(
    `/api/portal/community-navigator/providers/${id}/profile`,
    { method: "GET", headers: ohtScopeHeaders() }
  );
}

export type PatientContactType = "MeetAndGreet" | "Waitlist";

export type MarkPatientContactResult = {
  ok: boolean;
  alreadyRecorded?: boolean;
  contactType?: PatientContactType;
  providerRequestId?: number;
  outcomeCode?: string;
  navigatorCaseActivityId?: number | null;
  error?: string;
};

export async function markCommunityNavigatorPatientContact(
  navigatorCaseId: number,
  contactType: PatientContactType,
  providerRequestId: number
): Promise<MarkPatientContactResult> {
  const id = Number(navigatorCaseId);
  const requestId = Number(providerRequestId);
  if (!Number.isFinite(id) || id < 1) {
    throw new ApiError(400, "A valid case id is required.");
  }
  if (!Number.isFinite(requestId) || requestId < 1) {
    throw new ApiError(400, "A valid provider request id is required.");
  }

  return requestJson<MarkPatientContactResult>(
    `/api/portal/community-navigator/cases/${id}/patient-contact`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...ohtScopeHeaders(),
      },
      body: JSON.stringify({ contactType, providerRequestId: requestId }),
    }
  );
}
